import React, { useState, useRef, useEffect } from 'react';
import { Search, X, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SearchIcon = ({ 
  suggestions = [],
  onSearch,
  searchPath = '/products',
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Popular searches - matching actual products and categories
  const popularSearches = [
    'Dressers',
    'Wardrobes',
    'Wooden Beds',
    'Mattresses',
    'Sidetables'
  ];

  // Handle expand
  const handleExpand = () => {
    setIsExpanded(true);
    // Focus input after animation
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle collapse
  const handleCollapse = () => {
    setIsExpanded(false);
    setQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // Handle search submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      }
      navigate(`${searchPath}?search=${encodeURIComponent(query.trim())}`);
      handleCollapse();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    if (onSearch) {
      onSearch(suggestion);
    }
    navigate(`${searchPath}?search=${encodeURIComponent(suggestion)}`);
    handleCollapse();
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(value.length > 0);
    setSelectedIndex(-1);
    
    if (onSearch) {
      onSearch(value);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const displaySuggestions = suggestions.length > 0 ? suggestions : popularSearches;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < displaySuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(displaySuggestions[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      
      case 'Escape':
        handleCollapse();
        break;
      
      default:
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        if (isExpanded && query === '') {
          handleCollapse();
        }
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, query]);

  // Prevent body scroll when mobile overlay is open
  useEffect(() => {
    if (isExpanded) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  const displaySuggestions = suggestions.length > 0 ? suggestions : popularSearches;
  const suggestionLabel = suggestions.length > 0 ? 'Suggestions' : 'Popular Searches';

  return (
    <>
      {/* Desktop Search */}
      <div 
        ref={searchRef}
        className={`hidden md:block relative ${className}`}
      >
        {!isExpanded ? (
          /* Collapsed: Search Icon Only */
          <button
            onClick={handleExpand}
            className="p-2.5 hover:bg-primary-50 rounded-lg transition-all duration-300 group"
            aria-label="Open search"
          >
            <Search className="w-5 h-5 text-dark-700 group-hover:text-primary-600 transition-colors" />
          </button>
        ) : (
          /* Expanded: Full Search Bar */
          <div className="relative animate-expand-width">
            <form onSubmit={handleSubmit} className="relative">
              {/* Search Icon */}
              {/* <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" /> */}
              
              {/* Input */}
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search"
                className=" px-12 py-3 border-2 border-primary-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400 shadow-lg text-base"
                autoComplete="off"
              />
              
              {/* Close Button */}
              <button
                type="button"
                onClick={handleCollapse}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && displaySuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-slide-up">
                <div className="py-2">
                  {/* Header */}
                  <div className="px-4 py-2 flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
                    <TrendingUp className="w-4 h-4" />
                    {suggestionLabel}
                  </div>
                  
                  {/* Suggestions List */}
                  <div className="max-h-80 overflow-y-auto">
                    {displaySuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={`w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors flex items-center gap-3 ${
                          index === selectedIndex ? 'bg-primary-50' : ''
                        }`}
                      >
                        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-dark-800 font-medium">{suggestion}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Search */}
      <div className="md:hidden">
        {!isExpanded ? (
          /* Collapsed: Search Icon Only */
          <button
            onClick={handleExpand}
            className="p-2.5 hover:bg-primary-50 rounded-lg transition-colors"
            aria-label="Open search"
          >
            <Search className="w-5 h-5 text-dark-700" />
          </button>
        ) : (
          /* Expanded: Full-Screen Overlay */
          <div className="fixed inset-0 bg-white z-50 animate-fade-in">
            <div className="h-full flex flex-col">
              
              {/* Search Header */}
              <div className="flex items-center gap-3 p-4 border-b border-gray-200">
                <form onSubmit={handleSubmit} className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Search furniture..."
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 text-base"
                      autoComplete="off"
                      autoFocus
                    />
                  </div>
                </form>
                
                <button
                  onClick={handleCollapse}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  aria-label="Close search"
                >
                  <X className="w-6 h-6 text-dark-700" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchIcon;
